import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";

export default function Gallery({ items }) {
  const categories = useMemo(() => ["All", ...new Set(items.map((i) => i.category))], [items]);
  const [filter, setFilter] = useState("All");
  const [lightbox, setLightbox] = useState(null);
  const visible = filter === "All" ? items : items.filter((i) => i.category === filter);

  return (
    <section id="gallery" className="py-28 lg:py-36" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionHeading
          eyebrow="Gallery"
          title={<>Moments in <span className="gold-text">Gold</span></>}
          sub="Glimpses from the Treasure stage — pageants, runways, winners and everything behind the curtain."
          testId="gallery-heading"
        />

        <Reveal className="flex flex-wrap justify-center gap-3 mb-12" data-testid="gallery-filters">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              className={`rounded-full px-5 py-2 text-xs font-semibold tracking-widest uppercase transition-all duration-300 ${filter === c ? "bg-gold text-royal-950" : "border border-gold/30 text-muted hover:text-gold hover:border-gold/60"}`}
              data-testid={`gallery-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {c}
            </button>
          ))}
        </Reveal>

        <div className="columns-2 md:columns-3 gap-4" data-testid="gallery-masonry">
          {visible.map((item, i) => (
            <motion.button
              key={item.id}
              layout
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.04 * (i % 6) }}
              onClick={() => setLightbox(item)}
              className="group relative mb-4 w-full break-inside-avoid overflow-hidden rounded-md border border-gold/10 block"
              data-testid={`gallery-item-${item.id}`}
            >
              <img
                src={item.url}
                alt={`${item.caption} — ${item.category} at HA Treasure House`}
                loading="lazy"
                className="w-full object-cover transition-transform duration-[1100ms] group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-royal-950/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-5">
                <div className="text-left">
                  <p className="font-display text-lg text-gold-light">{item.caption}</p>
                  <p className="text-xs text-muted tracking-widest uppercase mt-1">{item.category}</p>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] bg-royal-950/95 backdrop-blur-md flex items-center justify-center p-5"
            onClick={() => setLightbox(null)}
            data-testid="gallery-lightbox"
          >
            <button className="absolute top-6 right-6 text-gold p-2 hover:rotate-90 transition-transform duration-300" aria-label="Close" data-testid="lightbox-close">
              <X size={30} />
            </button>
            <motion.figure
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-4xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={lightbox.url} alt={lightbox.caption} className="w-full max-h-[78vh] object-contain rounded-sm gold-frame" />
              <figcaption className="mt-5 text-center">
                <p className="font-display text-xl gold-text">{lightbox.caption}</p>
                <p className="text-xs text-muted tracking-widest uppercase mt-1">{lightbox.category}</p>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
